import { TFunction } from "i18next";
import React, { ReactNode } from "react";
import {
  SectionStyle,
  Setting,
  SettingButtonsDisable,
  Taxonomy,
} from "../types/types";
import { ColumnOptionsPanel } from "./ColumnOptionsPanel";
import { Column, Filter, FilterProps, Grouping } from "./types";

export type IDatePickerStrings = {
  months: string[];
  shortMonths: string[];
  days: string[];
  shortDays: string[];
  goToToday: string;
  prevMonthAriaLabel?: string;
  nextMonthAriaLabel?: string;
  prevYearAriaLabel?: string;
  nextYearAriaLabel?: string;
  closeButtonAriaLabel?: string;
};

export type Strings = {
  headerLabel?: string;
  applyButtonLabel?: string;
  resetButtonLabel?: string;
  newViewButtonLabel?: string;
  editViewButtonLabel?: string;
  deleteViewButtonLabel?: string;
  /** clear all filters in context menu */
  clearFiltersButtonLabel?: string;
  /** Save view in context menu */
  saveViewButtonLabel?: string;
  visibilityCheckboxTitle?: string;
  visibilitySelectAllTitle?: string;
  visibilityDeselectAllTitle?: string;
  dragHandleTitle?: string;
  /** clear individual filter button in filter dropdown */
  clearFilterButtonLabel?: string;
  /** close (X) button in filter dropdown */
  closeButtonLabel?: string;
  dateFilterOperatorIsAfterLabel?: string;
  dateFilterOperatorIsBeforeLabel?: string;
  dateFilterOperatorIsExactLabel?: string;
  dateFilterOperatorIsBetweenLabel?: string;
  numberFilterOperatorGtLabel?: string;
  numberFilterOperatorLtLabel?: string;
  numberFilterOperatorEqLabel?: string;
  numberFilterOperatorBetweenLabel?: string;
  numberFilterIncludeLabel?: string;
  numberFilterAndLabel?: string;
  booleanFilterTrueLabel?: string;
  booleanFilterFalseLabel?: string;
  textFilterLabel?: string;
  createViewDialogLabel?: string;
  editViewDialogLabel?: string;
  viewDialogSaveButtonLabel?: string;
  requiredErrorMessage?: string;
  viewExistsErrorMessage?: string;
  viewDialogNameFieldLabel?: string;
  viewDialogDescriptionFieldLabel?: string;
  viewDialogFavoriteFieldLabel?: string;
  viewSelectNoViewsLabel?: string;
  viewSelectPlaceholder?: string;
  viewSelectLoadingLabel?: string;
  datePickerStrings?: IDatePickerStrings;
};

type Props = {
  id?: string;
  closePanel: () => void;
  columns: Column[];
  onClickApply: () => void;
  views: Setting[];
  selectedView: Setting | null;
  onChangeView: (userAppSettingId: Setting["userAppSettingId"] | null) => void;
  /** If isReady is false, column options panel will be blocked and a spinner will be shown in the middle */
  isReady: boolean;
  /** This should be true if we are waiting for any backend operation regarding views (fetching, creating, updating, deleting).
   * A spinner will be shown in the view dropdown  */
  isViewLoading: boolean;
  setFilter: (columnKey: string, filter: Filter) => void;
  maxSelection?: number;
  /** When the user clicks on the select/deselect all checkbox if it is not checked, this is called.
   * You should set all columns in the list to visible in this callback. */
  onSelectAll: () => void;
  /** When the user clicks on the select all checkbox if it is checked, this is called.
   * You should set all columns in the list to not visible in this callback. */
  onDeselectAll: () => void;
  /** Object containing all taxonomies that the column filters use (or more).
   * The format is the same as we get from the server */
  taxonomy: {
    [key: string]: Taxonomy[];
  };
  onClickDeleteView: () => void;
  onClickClearAllFilters: () => void;
  /** You should only reset the columns state that you pass to this component.
   *  If a view is selected, reset to the columns of that view, if not, reset to the default column configuration */
  onClickReset: () => void;
  onClickSaveView: () => void;
  onCreateView: (data: {
    userAppSettingName: string;
    userAppSettingDescription: string;
    isDefault: boolean;
  }) => void;
  onUpdateView: (data: {
    userAppSettingName: string;
    userAppSettingDescription: string;
    isDefault: boolean;
  }) => void;
  onReorderColumn: (sourceIndex: number, destinationIndex: number) => void;
  onToggleColumnVisibility: (columnKey: string) => void;
  /** e.g. show deleted claims, tasks .... */
  renderAdditionalOptions?: () => ReactNode;
  /** Height of the container to render additional options into. Defaults to 80px */
  additionalOptionsHeight?: number;
  /** Other styles of the container to render additional options into. */
  additionalOptionsStyle?: SectionStyle;
  strings?: Strings;
  t: TFunction;
  hideViewOptionsFeatures?: boolean;
  /** Object to disable view buttons, use it only if you want to disable button(s), it has priority under internal button(s) disable logic.
   * Example: {new: true, save: true, edit: true, reset: true, clearAllFilters: true, delete: true }
   */
  disableButtons?: SettingButtonsDisable;
  /** Use it to pass additional props definition for filter fields. Currently implemented only for filters with type 'date', but at a same way can be added to all other filter types.
   * Example: {createdAt: {data1: {dataPicker props}, date2: {datePicker props}}
   */
  filterProps?: FilterProps;
  /** Indicator should placeholder be used when view select is empty */
  usePlaceholder?: boolean;
  columnOptionsRef?: any;
  /**This is a flag for disabling creating new view option in ColumnOptions */
  disableNewView?: boolean;
  /** "Group by" criteria offered in the available-fields pane. If omitted, the
   * available pane shows a single flat, alphabetically sorted list. */
  groupings?: Grouping[];
  /** True when the current columns differ from the selected view's saved
   * configuration. Shows an "unsaved changes" indicator and emphasizes the
   * "Save current view" action until the user saves. */
  hasUnsavedChanges?: boolean;
};

const stringsDefaults = (t: TFunction): Required<Strings> => {
  return {
    headerLabel: t("greco.columnOptions"),
    booleanFilterTrueLabel: t("true"),
    booleanFilterFalseLabel: t("false"),
    applyButtonLabel: t("greco.apply"),
    dateFilterOperatorIsAfterLabel: t("greco.isAfter"),
    dateFilterOperatorIsBeforeLabel: t("greco.isBefore"),
    dateFilterOperatorIsExactLabel: t("greco.isExact"),
    dateFilterOperatorIsBetweenLabel: t("greco.isBetween"),
    numberFilterOperatorGtLabel: t("numberFilterOperator.gt"),
    numberFilterOperatorLtLabel: t("numberFilterOperator.lt"),
    numberFilterOperatorEqLabel: t("numberFilterOperator.eq"),
    numberFilterOperatorBetweenLabel: t("numberFilterOperator.between"),
    numberFilterIncludeLabel: t("columnOptions.include"),
    numberFilterAndLabel: t("greco.and"),
    dragHandleTitle: t("grid.ReorderColumn"),
    visibilityCheckboxTitle: t("grid.ShowHideColumn"),
    visibilitySelectAllTitle: t("grid.SelectAll"),
    visibilityDeselectAllTitle: t("grid.DeselectAll"),
    requiredErrorMessage: t("greco.required"),
    viewExistsErrorMessage: t("greco.errors.exists", {
      object: t("greco.view"),
    }),
    viewDialogNameFieldLabel: t("greco.name"),
    viewDialogDescriptionFieldLabel: t("greco.description"),
    viewDialogFavoriteFieldLabel: t("greco.favorite"),
    viewDialogSaveButtonLabel: t("greco.save"),
    editViewDialogLabel: t("greco.editView"),
    createViewDialogLabel: t("greco.createView"),
    saveViewButtonLabel: t("greco.saveCurrentView"),
    editViewButtonLabel: t("greco.editCurrentView"),
    resetButtonLabel: t("greco.reset"),
    clearFiltersButtonLabel: t("greco.clearFilters"),
    deleteViewButtonLabel: t("greco.deleteCurrentView"),
    newViewButtonLabel: t("greco.newView"),
    clearFilterButtonLabel: t("greco.clear"),
    closeButtonLabel: t("greco.close"),
    textFilterLabel: t("greco.containsText"),
    viewSelectNoViewsLabel: t("greco.noViews"),
    viewSelectPlaceholder: t("greco.selectView"),
    viewSelectLoadingLabel: t("greco.loading"),
    datePickerStrings: {
      months: [
        t("datePicker.January"),
        t("datePicker.February"),
        t("datePicker.March"),
        t("datePicker.April"),
        t("datePicker.May"),
        t("datePicker.June"),
        t("datePicker.July"),
        t("datePicker.August"),
        t("datePicker.September"),
        t("datePicker.October"),
        t("datePicker.November"),
        t("datePicker.December"),
      ],

      shortMonths: [
        t("datePicker.short.January"),
        t("datePicker.short.February"),
        t("datePicker.short.March"),
        t("datePicker.short.April"),
        t("datePicker.short.May"),
        t("datePicker.short.June"),
        t("datePicker.short.July"),
        t("datePicker.short.August"),
        t("datePicker.short.September"),
        t("datePicker.short.October"),
        t("datePicker.short.November"),
        t("datePicker.short.December"),
      ],

      days: [
        t("datePicker.Sunday"),
        t("datePicker.Monday"),
        t("datePicker.Tuesday"),
        t("datePicker.Wednesday"),
        t("datePicker.Thursday"),
        t("datePicker.Friday"),
        t("datePicker.Saturday"),
      ],

      shortDays: [
        t("datePicker.short.Sunday"),
        t("datePicker.short.Monday"),
        t("datePicker.short.Tuesday"),
        t("datePicker.short.Wednesday"),
        t("datePicker.short.Thursday"),
        t("datePicker.short.Friday"),
        t("datePicker.short.Saturday"),
      ],

      goToToday: t("datePicker.goToToday"),
      prevMonthAriaLabel: t("datePicker.prevMonthAriaLabel"),
      nextMonthAriaLabel: t("datePicker.nextMonthAriaLabel"),
      prevYearAriaLabel: t("datePicker.prevYearAriaLabel"),
      nextYearAriaLabel: t("datePicker.nextYearAriaLabel"),
      closeButtonAriaLabel: t("datePicker.closeButtonAriaLabel"),
    },
  };
};

export const ColumnOptionsContext = React.createContext<
  Props & { strings: Required<Strings> }
>({
  ...({} as Props),
  columns: [],
  strings: {} as any,
});

export const ColumnOptions = (props: Props) => {
  return (
    <ColumnOptionsContext.Provider
      value={{
        ...props,
        strings: { ...stringsDefaults(props.t), ...props.strings },
        additionalOptionsHeight: props.additionalOptionsHeight ?? 80,
        additionalOptionsStyle: props.additionalOptionsStyle,
        disableButtons: props.disableButtons,
        filterProps: props.filterProps,
        usePlaceholder: props.usePlaceholder,
        columnOptionsRef: props.columnOptionsRef,
      }}
    >
      <ColumnOptionsPanel />
    </ColumnOptionsContext.Provider>
  );
};
